// @flow

import * as React from 'react';

import { useSocialLoginGate } from '@ta/social.login-gate';
import { featureIsEnabled } from '@ta/common.features';
import type { TripsScopedError } from '@ta/trips.trip-errors';
import { useLocalizeTripError } from '@ta/trips.trip-errors';
import * as TripTypes from '@ta/trips.trip-types';
import {
  getInteractionsContext,
  type LogInteractionFunction,
  SaveSessionProvider,
  SaveSessionTrackingContext,
  type TripsContext,
  type TripInteractionSources,
  useTripInteraction,
} from '@ta/trips.tracking';
import { toSavesObject } from '@ta/trips.trip-util';
import useTripToast, { type MessageType } from '@ta/trips.trip-toasts';
import { useAutosave } from '@ta/trips.saver';
import { Route, Visitor } from '@ta/lithium.context';
import { usePixelCallback } from '@ta/platform.monikers';

import type { OverlayState } from './STATModal';
import STATModal from './STATModal';

type ExternalProps = {|
  object: any,
  /** whether or not button is "active". "active" means the item is already saved to a list */
  active: boolean,
  /** Prevents interaction logging. Useful when the interaction has been logged upstream, such as in SaveAction on SocialBar */
  preventInteractionPropagation?: boolean,
  /** Context to use for tracking */
  trackingContext?: TripsContext,
  /** Source to use for tracking */
  trackingSource?: TripInteractionSources,
  /** A render prop which can open the trip flow. */
  children: (openFlow: () => mixed) => React.Node,
  /** Optional callback that fires when the user has finished interacting with the flow. */
  onComplete?: (isActive: boolean) => void,
  /** Optional: The originating Trip if the item is being re-saved */
  resaveTrip?: TripTypes.Trip,
  /** Optional: If automatically popping the STAT modal via URL hash is allowed */
  allowPopStatModal?: boolean,
|};

type InteractionProps = {|
  logInteraction: LogInteractionFunction,
  puid: string,
|};

/** Props for the session wrapper */
type WrapperProps = {|
  ...ExternalProps,
|};

/** All props: external, wrapped session, interaction, and Redux */
type Props = {|
  ...WrapperProps,
  ...InteractionProps,
  saveObject: TripTypes.TripSaveObject,
|};

type State = {|
  overlayState: OverlayState,
  preventInteractionInMountedComponents: boolean,
  statModalVisible: boolean,
|};

type Action = {|
  type: 'openModal' | 'closeModal' | 'setOverlayState' | 'openSelectModal' | 'afterSave',
  state?: OverlayState,
|};

const SOCIAL_PAGES = ['Profile', 'HomeFeed', 'Trips', 'Home'];

const statReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'openModal':
      return {
        ...state,
        statModalVisible: true,
      };
    case 'closeModal':
      return {
        ...state,
        overlayState: 'DEFAULT',
        preventInteractionInMountedComponents: false,
        statModalVisible: false,
      };
    case 'setOverlayState':
      return {
        ...state,
        overlayState: action.state,
      };
    case 'openSelectModal':
      return {
        ...state,
        overlayState: 'SELECT',
        statModalVisible: true,
      };
    case 'afterSave':
      return {
        ...state,
        overlayState: 'DEFAULT',
        preventInteractionInMountedComponents: true,
      };
    default:
      throw new Error();
  }
};

/**
 * Button for saving an item (POI, etc) to a trip.
 * Dictates which saves flow to enter.
 */
const SaveToTripButton = ({
  allowPopStatModal,
  children,
  logInteraction,
  puid,
  object,
  active,
  preventInteractionPropagation,
  trackingContext,
  trackingSource,
  resaveTrip,
  saveObject,
  ...props
}: Props): React.Node => {
  const { userId: loggedInUserId } = React.useContext(Visitor);
  const [route] = React.useContext(Route);
  const {
    launchViewTripToast,
    launchRemoveFromTripToast,
    launchAutoSavedToTripToast,
    launchErrorTripToast,
  } = useTripToast();

  const { getTripErrorMessage } = useLocalizeTripError();

  const fireSaveToTripIntent = usePixelCallback('SAVE_TO_TRIP_INTENT');
  const fireSaveToTrip = usePixelCallback('SAVE_TO_TRIP');
  const fireCreateTrip = usePixelCallback({
    type: 'CREATE_TRIP',
    payload: { user_id: loggedInUserId || '' },
  });

  const { page } = route;
  const saveSessionTracking = React.useContext(SaveSessionTrackingContext);

  const autoPopSTAT = React.useMemo(() => route.params.popStat || false, [route.params]);

  const [state, dispatch] = React.useReducer<State, Action>(statReducer, {
    overlayState: 'DEFAULT',
    preventInteractionInMountedComponents: false,
    statModalVisible: false,
  });

  const onCreateTripClicked = () => dispatch({ type: 'setOverlayState', state: 'CREATE' });
  const onSelectCreateTripClicked = () =>
    dispatch({ type: 'setOverlayState', state: 'SELECT_CREATE' });
  const openModal = React.useCallback(() => dispatch({ type: 'openModal' }), []);

  const popStatEnabled = React.useCallback(
    () => !!allowPopStatModal && autoPopSTAT && featureIsEnabled('trips_pop_stat_modal'),
    [allowPopStatModal, autoPopSTAT],
  );

  const { onComplete: externalOnComplete } = props;
  const onComplete = React.useCallback(
    (isActive: boolean) => {
      if (externalOnComplete) {
        externalOnComplete(isActive);
      }
      saveSessionTracking.resetSession();
    },
    [externalOnComplete, saveSessionTracking],
  );

  const onUndoRemoveAction = React.useCallback(
    (trip: ?TripTypes.Trip, error: ?TripsScopedError) => {
      if (trip && !error) {
        const onUndoClick = () => {
          logInteraction({ elementClick: { element: 'saved_viewTrip' } });
        };
        launchViewTripToast(trip, 'SAVED', onUndoClick);
        onComplete(true);
      } else if (error) {
        launchErrorTripToast(error);
        onComplete(false);
      }
      dispatch({ type: 'setOverlayState', state: 'DEFAULT' });
      logInteraction({ elementClick: { element: 'unsave_undo' } });
    },
    [launchViewTripToast, onComplete, launchErrorTripToast, logInteraction],
  );

  const showUndoRemoveToast = React.useCallback(
    (trip: TripTypes.Trip) => {
      launchRemoveFromTripToast(trip, saveObject, onUndoRemoveAction);
    },
    [launchRemoveFromTripToast, onUndoRemoveAction, saveObject],
  );

  const onSaveError = React.useCallback(
    (scopedError: TripsScopedError) => {
      launchErrorTripToast(scopedError);
    },
    [launchErrorTripToast],
  );

  const onModalClose = React.useCallback(() => {
    dispatch({ type: 'closeModal' });
    saveSessionTracking.resetSession();
  }, [saveSessionTracking]);

  const [saveStatus, save, updateLastTripId] = useAutosave(saveObject, active, openModal);

  const onSuccessfulSave = React.useCallback(
    (trip: TripTypes.Trip, messageType: MessageType) => {
      // If the user has just created a trip to save to, fire the saves tracking pixel
      if (messageType === 'CREATED') {
        fireSaveToTrip();
        fireCreateTrip();
      }

      dispatch({ type: 'afterSave' });
      launchViewTripToast(trip, messageType, () => {
        logInteraction({ elementClick: { element: 'saved_viewTrip' } });
      });
      updateLastTripId(trip.id);
      onComplete(true);
    },
    [
      fireSaveToTrip,
      launchViewTripToast,
      onComplete,
      updateLastTripId,
      fireCreateTrip,
      logInteraction,
    ],
  );

  const onLoginStart = React.useCallback(() => {
    logInteraction({
      elementClick: {
        element: 'ITEM_SAVE_LOGIN',
        context: page ? getInteractionsContext(page) || undefined : undefined,
        puid,
        saveType: saveObject.type,
        detailId: saveObject.id ? `${saveObject.id}` : '',
        tripId: resaveTrip?.id,
        sessionType: 'SAVE',
        sessionUuid: saveSessionTracking.sessionUuid,
        sessionOrder: saveSessionTracking.sessionOrder,
      },
    });
  }, [
    logInteraction,
    page,
    puid,
    resaveTrip,
    saveObject.id,
    saveObject.type,
    saveSessionTracking.sessionOrder,
    saveSessionTracking.sessionUuid,
  ]);

  const onLoginSuccess = React.useCallback(() => {
    logInteraction({
      modalSuccess: {
        element: 'ITEM_SAVE_LOGIN',
        context: page ? getInteractionsContext(page) || undefined : undefined,
        puid,
        saveType: saveObject.type,
        detailId: saveObject.id ? `${saveObject.id}` : '',
        tripId: resaveTrip?.id,
        sessionType: 'SAVE',
        sessionUuid: saveSessionTracking.sessionUuid,
        sessionOrder: saveSessionTracking.sessionOrder,
      },
    });
  }, [
    logInteraction,
    page,
    puid,
    resaveTrip,
    saveObject.id,
    saveObject.type,
    saveSessionTracking.sessionOrder,
    saveSessionTracking.sessionUuid,
  ]);

  const authRequest = React.useMemo(
    () => ({
      pid: page && SOCIAL_PAGES.includes(route.page) ? 40439 : 39766,
      flow: 'SAVES_COMBINED',
      onLogin: onLoginStart,
      onSuccess: onLoginSuccess,
    }),
    [onLoginStart, onLoginSuccess, page, route.page],
  );

  /**
   * The flag indicating that autopop for STAT modal has been already processed.
   */
  const autoPopStatTriggered = React.useRef(false);

  const loginGate = useSocialLoginGate(authRequest);
  React.useEffect(() => {
    if (popStatEnabled() && !autoPopStatTriggered.current) {
      autoPopStatTriggered.current = true;
      logInteraction({ elementClick: { element: 'autoPopStat' } });
      loginGate().then(openModal);
    }
  }, [logInteraction, loginGate, openModal, popStatEnabled]);

  const gatedSave = React.useCallback(() => {
    loginGate()
      .then(() => save())
      .then(([trip, tripsError]) => {
        if (tripsError) {
          onSaveError({ query: 'ADD_ITEM_TO_TRIP', tripsError });
        } else if (trip) {
          const onCtaClick = () => {
            dispatch({ type: 'openSelectModal' });
            logInteraction({ elementClick: { element: 'quickSave_change' } });
          };
          launchAutoSavedToTripToast(trip, onCtaClick);
          dispatch({ type: 'setOverlayState', state: 'DEFAULT' });
          onComplete(true);
        }
      });
  }, [launchAutoSavedToTripToast, logInteraction, loginGate, onComplete, onSaveError, save]);

  return (
    <div
      className="stat_wrapper"
      onClick={(e: SyntheticEvent<*>) => {
        e.stopPropagation();
        e.preventDefault();

        /*
         * This event should be logged only if user is logged in and preventInteractionPropagation is not active
         * If user is not logged in, the event will be logged by SocialLoginGate
         * */
        if (!preventInteractionPropagation && loggedInUserId) {
          logInteraction({
            elementClick: {
              element: 'item_save',
              tripId: resaveTrip?.id,
            },
          });
        }

        if (!active) {
          fireSaveToTripIntent();
        }
      }}
    >
      {children(gatedSave)}
      {state.statModalVisible && loggedInUserId && (
        <STATModal
          saveObject={saveObject}
          overlayState={state.overlayState}
          lastSavedItem={saveStatus.result?.tripItem}
          trackingContext={trackingContext}
          trackingSource={trackingSource}
          onCreateTripClicked={onCreateTripClicked}
          onSelectCreateTripClicked={onSelectCreateTripClicked}
          onClose={onModalClose}
          onError={onSaveError}
          preventInteractionInMountedComponents={state.preventInteractionInMountedComponents}
          statCallback={(itemAction, tripAction, trip, saved, isActive) => {
            if (itemAction === 'REMOVE') {
              showUndoRemoveToast(trip);
              onComplete(isActive);
            } else if (tripAction === 'NEW') {
              onSuccessfulSave(trip, 'CREATED');
            } else if (saved) {
              onSuccessfulSave(trip, 'SAVED');
            }
          }}
          validateTripName={name => {
            if (
              saveObject.type === 'bundleSave' &&
              name.trim().toLowerCase() === object.title.trim().toLowerCase()
            ) {
              return getTripErrorMessage('COPY_TRIP', {
                type: 'INVALID_VALUE',
                field: 'TITLE',
              });
            }
            return undefined;
          }}
        />
      )}
    </div>
  );
};

SaveToTripButton.defaultProps = {
  preventInteractionPropagation: false,
  onComplete: undefined,
  trackingContext: undefined,
  allowPopStatModal: false,
};

const FeatureGatedSTATButton = (props: Props) => {
  if (!featureIsEnabled('trips_bookmark')) {
    return null;
  }
  return <SaveToTripButton {...props} />;
};

/**
 * Wraps the SaveToTripButton with SaveSessionProvider to give it access to the save session.
 */
const TripFlowSelectorWrapper = (props: WrapperProps) => {
  const saveObject = React.useMemo(() => toSavesObject(props.object, true), [props.object]);

  const interactionParams = React.useMemo(
    () => ({
      source: props.trackingSource || 'trips',
      saveObject,
      trackingContext: props.trackingContext,
      withSaveSession: true,
      pageAsContext: true,
    }),
    [props.trackingContext, props.trackingSource, saveObject],
  );
  const [logInteraction, puid] = useTripInteraction(interactionParams);

  if (!saveObject) {
    return null;
  }
  return (
    <SaveSessionProvider>
      <FeatureGatedSTATButton
        {...props}
        logInteraction={logInteraction}
        puid={puid}
        saveObject={saveObject}
      />
    </SaveSessionProvider>
  );
};

export default TripFlowSelectorWrapper;
